import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, ConfirmationModal, NotFoundState, LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import {
    AdvancedFilterSortModal,
    ReportVariablesBar,
    ReportRowDetailsModal,
    ReportRunVariablesModal,
    ReportResultContainer
} from "@/components/reports";
import { ItemDetailModal } from "@/components";
import {
    LoadingOverlay,
} from "@mantine/core";
import {
    extractVariablesFromUrl,
    validateVariables,
} from "@bi-studio/shared/lib";
import { getFolderForItem, deleteReport, executeReport } from "@bi-studio/shared/apis";
import { Search } from "lucide-react";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    fetchReportDetails,
    executeReportDataThunk,
    setVariableValues,
    setSortConfigs,
    setAdvancedFilters,
    setIsExporting,
    resetReportState,
    selectReportFull,
    selectFilteredRows
} from "@/store/slices/reportSlice";
import { PageActions, SortFilterReportButton, ReportActionsButton, PageContainer } from "@/components";

export default function ReportDetailPage() {
    const { isAuthenticated, isLoading: authLoading, token, user } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id: reportId } = useParams();
    const [searchParams] = useSearchParams();
    const mode = (searchParams.get("mode") as "view" | "edit" | null) || "view";
    const isEmbed = searchParams.get("embed") === "true";

    // Redux State
    const {
        report,
        previewResult,
        variableValues,
        requiredVariables,
        sortConfigs,
        advancedFilters,
        page,
        pageSize,
        isLoading,
        isExporting,
        error
    } = useAppSelector(selectReportFull);

    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
    const [isVariableModalOpen, setIsVariableModalOpen] = React.useState(false);
    const [isAdvancedModalOpen, setIsAdvancedModalOpen] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState<Record<string, unknown> | null>(null);
    const [isRowModalOpen, setIsRowModalOpen] = React.useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    // Initial load and cleanup
    React.useEffect(() => {
        if (token && reportId) {
            dispatch(fetchReportDetails({ token, reportId, companyId: user?.companyId }));
        }
        return () => {
            dispatch(resetReportState());
        };
    }, [token, reportId, user?.companyId, dispatch]);

    // Handle variables from URL
    React.useEffect(() => {
        if (report && requiredVariables?.length > 0) {
            const urlValues = extractVariablesFromUrl(searchParams, requiredVariables);
            if (Object.keys(urlValues).length > 0) {
                dispatch(setVariableValues({ ...variableValues, ...urlValues }));
            }

            const { valid } = validateVariables(requiredVariables, { ...variableValues, ...urlValues });
            if (!valid && mode === "view") {
                setIsVariableModalOpen(true);
            }
        }
    }, [report, requiredVariables, searchParams, mode, dispatch]);

    const handleExecute = React.useCallback(async (values: Record<string, string> = variableValues, cache: boolean = false, newPage: number = 1) => {
        if (!token || !reportId) return;

        if (requiredVariables?.length > 0) {
            const { valid } = validateVariables(requiredVariables, values);
            if (!valid) {
                if (mode === "view") setIsVariableModalOpen(true);
                return;
            }
        }

        dispatch(executeReportDataThunk({
            token,
            reportId,
            variables: values,
            cache,
            pagination: { page: newPage, pageSize }
        }));
        setIsVariableModalOpen(false);
    }, [token, reportId, requiredVariables, variableValues, pageSize, mode, dispatch]);

    // Auto-execute logic
    React.useEffect(() => {
        if (mode === "view" && reportId && token && report && !isLoading && requiredVariables?.length === 0 && !previewResult) {
            handleExecute(variableValues, false, page);
        }
    }, [mode, reportId, token, report, isLoading, requiredVariables?.length, previewResult, handleExecute, variableValues, page]);

    const filteredRows = useAppSelector(selectFilteredRows);

    const handleRowClick = (row: Record<string, unknown>) => {
        setSelectedRow(row);
        setIsRowModalOpen(true);
    };

    const columnUniqueValues = React.useMemo(() => {
        const rows = previewResult?.rows;
        const columns = previewResult?.columns;
        if (!rows || !columns) return {};
        const uniqueValues: Record<string, string[]> = {};
        columns.forEach(col => {
            const values = new Set<string>();
            rows.forEach(row => {
                const val = row[col.name];
                if (val !== null && val !== undefined) {
                    values.add(typeof val === 'object' && !(val instanceof Date) ? JSON.stringify(val) : String(val));
                }
            });
            uniqueValues[col.name] = Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        });
        return uniqueValues;
    }, [previewResult]);

    const handleBack = React.useCallback(async () => {
        if (!token || !reportId) { navigate(-1); return; }
        try {
            const folderInfo = await getFolderForItem(token, "report", reportId);
            navigate(folderInfo.folderId ? `/folders/${folderInfo.folderId}` : "/folders");
        } catch (err) { navigate(-1); }
    }, [token, reportId, navigate]);

    const handleDeleteReport = async () => {
        if (!token || !reportId) return;
        try {
            setIsDeleting(true);
            await deleteReport(token, reportId);
            const folderId = searchParams.get("folderId");
            navigate(folderId ? `/folders/${folderId}` : "/folders");
        } catch (err) {
            setIsDeleteModalOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    // Exports
    const getExportFileName = (extension: string) => `${report?.name || "report"}_${new Date().toISOString().split("T")[0]}.${extension}`;
    const downloadData = (data: any, fileName: string, type: 'csv' | 'xlsx' | 'json') => {
        if (type === 'json') {
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        } else if (type === 'csv') {
            if (data.length === 0) return;
            const headers = Object.keys(data[0]);
            const csv = [headers.join(','), ...data.map((row: any) => headers.map((h: any) => {
                const v = String(row[h] || '').replace(/"/g, '""');
                return v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v}"` : v;
            }).join(','))].join('\n');
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        } else if (type === 'xlsx') {
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
            XLSX.writeFile(wb, fileName);
        }
    };

    const handleExport = async (type: 'csv' | 'xlsx' | 'json', allRows: boolean) => {
        if (!token || !reportId || !previewResult) return;
        dispatch(setIsExporting(true));
        try {
            let dataToExport = previewResult.rows;
            if (allRows) {
                const result = await executeReport(token, reportId, { variables: variableValues, cache: { enabled: false }, pagination: { page: 1, pageSize: 0 } });
                dataToExport = result.rows;
            }
            downloadData(dataToExport, getExportFileName(type === 'xlsx' ? 'xlsx' : type), type);
        } finally {
            dispatch(setIsExporting(false));
        }
    };

    const isReady = !!report;
    const isPageLoading = isLoading || authLoading;

    if (!isAuthenticated) return null;

    if (!isReady && isPageLoading) {
        return (
            <AppLayout onLogout={handleLogout}>
                <LoadingScreen message="Loading report..." />
            </AppLayout>
        );
    }

    if (!report) {
        const fallback = (
            <NotFoundState
                title="Report Not Found"
                message="The report you are looking for does not exist or you don't have permission to view it."
                icon={<Search size={32} />}
                showAction={!isEmbed}
                actionLabel="Back to Folders"
                onAction={() => navigate('/folders')}
            />
        );
        return isEmbed ? fallback : <AppLayout onLogout={handleLogout}>{fallback}</AppLayout>;
    }

    const reportContent = (
        <PageContainer className={isEmbed ? "p-2" : ""}>
            <LoadingOverlay visible={isLoading && isReady} />
            <PageActions title={report.name} onBack={!isEmbed ? handleBack : undefined}>
                {!isEmbed && <SortFilterReportButton onClick={() => setIsAdvancedModalOpen(true)} />}
                {!isEmbed && (
                    <ReportActionsButton
                        setIsDetailsOpen={setIsDetailsOpen}
                        setIsDeleteModalOpen={setIsDeleteModalOpen}
                        handleExecute={handleExecute}
                    />
                )}
            </PageActions>

            {error && (
                <ErrorState title="Error" message={error} showAction={false} />
            )}

            {/* Variables Bar if horizontal */}
            {mode === "view" && !isVariableModalOpen && (
                <ReportVariablesBar
                    onRun={(values) => handleExecute(values, false, 1)}
                />
            )}

            <ReportResultContainer
                rowCount={previewResult?.rowCount || 0}
                filteredRowsCount={filteredRows.length}
                isExporting={isExporting}
                onExport={handleExport}
                onRowClick={handleRowClick}
                hideExport={isEmbed}
                hidePagination={isEmbed}
            />

            <ItemDetailModal
                opened={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                itemType="report"
                itemId={reportId ?? null}
            />

            <ReportRunVariablesModal
                opened={isVariableModalOpen}
                onClose={() => setIsVariableModalOpen(false)}
                onRun={(values) => handleExecute(values, false, 1)}
            />

            <AdvancedFilterSortModal
                opened={isAdvancedModalOpen}
                onClose={() => setIsAdvancedModalOpen(false)}
                columns={previewResult?.columns || []}
                sortConfigs={sortConfigs}
                filterRules={advancedFilters}
                onApply={(s, f) => {
                    dispatch(setSortConfigs(s));
                    dispatch(setAdvancedFilters(f));
                }}
                columnUniqueValues={columnUniqueValues}
            />

            <ReportRowDetailsModal
                opened={isRowModalOpen}
                onClose={() => setIsRowModalOpen(false)}
                selectedRow={selectedRow}
            />

            <ConfirmationModal
                opened={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteReport}
                title="Delete Report"
                confirmLabel="Delete"
                confirmColor="red"
                loading={isDeleting}
            >
                {`Are you sure you want to delete "${report.name}"? This action cannot be undone.`}
            </ConfirmationModal>
        </PageContainer>
    );

    return isEmbed ? reportContent : <AppLayout onLogout={handleLogout}>{reportContent}</AppLayout>;
}
